import type { CommendaAiMessageMetadata } from '@commenda/api/commenda-ai/types';

/** Reads what the backend stored alongside a message, and turns it into what the row shows. */
export class CommendaAiMessageHelper {
  static readonly MINUTE_MS = 60 * 1000;

  static readonly HOUR_MS = 60 * CommendaAiMessageHelper.MINUTE_MS;

  static readonly DAY_MS = 24 * CommendaAiMessageHelper.HOUR_MS;

  /** `UIMessage['metadata']` is `unknown`: a message streamed in this session carries none. */
  static readMetadata(metadata: unknown): CommendaAiMessageMetadata {
    if (typeof metadata !== 'object' || metadata === null) return {};

    const read: CommendaAiMessageMetadata = {};

    if ('createdAt' in metadata && typeof metadata.createdAt === 'string')
      read.createdAt = metadata.createdAt;
    if ('senderName' in metadata && typeof metadata.senderName === 'string')
      read.senderName = metadata.senderName;
    if ('timelineBlocks' in metadata && Array.isArray(metadata.timelineBlocks))
      read.timelineBlocks = metadata.timelineBlocks;

    return read;
  }

  /** Age of a message in the words the design uses: "Just now", "3 hours ago", "2 days ago".
   *
   * A message sent in this session has no stored timestamp yet, which is exactly when "Just now"
   * is true, so the missing value needs no special case at the call site. */
  static formatSentAt(
    createdAt: string | undefined,
    now: number = Date.now()
  ): string {
    if (!createdAt) return 'Just now';

    const sentAt = new Date(createdAt).getTime();

    if (Number.isNaN(sentAt)) return 'Just now';

    const elapsed = Math.max(0, now - sentAt);
    const plural = (count: number, unit: string) =>
      `${count} ${unit}${count === 1 ? '' : 's'} ago`;

    if (elapsed < CommendaAiMessageHelper.MINUTE_MS) return 'Just now';
    if (elapsed < CommendaAiMessageHelper.HOUR_MS)
      return plural(
        Math.floor(elapsed / CommendaAiMessageHelper.MINUTE_MS),
        'minute'
      );
    if (elapsed < CommendaAiMessageHelper.DAY_MS)
      return plural(
        Math.floor(elapsed / CommendaAiMessageHelper.HOUR_MS),
        'hour'
      );
    if (elapsed < 7 * CommendaAiMessageHelper.DAY_MS)
      return plural(
        Math.floor(elapsed / CommendaAiMessageHelper.DAY_MS),
        'day'
      );

    return new Date(sentAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
}
